import React, { ReactElement } from 'react';
import './prispevokVestnikaDetail.scss';
import { Button, Heading } from 'idsk-react-components';
import { useNavigate } from 'react-router-dom';
import { ConfirmModal, UploadedFile } from 'slovlex-idsk-react-components';
import PrispevokVestnikaDetailRow from './component/PrispevokVestnikaDetailRow';
import mapDate from '../../function/mapDate';
import iconEdit from '../../../assets/images/icon-edit-black.svg';
import iconBin from '../../../assets/images/icon-bin-black.svg';
import routes from '../../routing/routes';
import UserRole from '../../model/currentuser/UserRole';
import Secured from '../Secured';
import PrispevokVestnika from '../../model/PrispevokVestnika';
import { handleDownloadPriloha } from '../../function/handleDownloadPriloha';
import { isOwnVestnikOrganization } from '../../function/permissions';
import { useUserContext } from '../../context/UserContext';
import { useSecurityContext } from '../../context/SecurityContext';
import { deletePrispevokVestnika } from '../../client/prispevokVestnikaDeleteClient';
import { useClient } from '../../context/ClientStatusContext';

interface Props {
  vestnikDetail: PrispevokVestnika;
}

const PrispevokVestnikaDetail = ({
  vestnikDetail,
}: Props): ReactElement => {
  const navigate = useNavigate();
  const hlavnyDokument = vestnikDetail.prilohy?.find(priloha => priloha.hlavna);
  const prilohy = vestnikDetail.prilohy?.filter(priloha => !priloha.hlavna);
  const { pouzivatel } = useUserContext();
  const { accessToken, organizationId } = useSecurityContext();
  const headers = {
    ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
    ...(organizationId && { OrganizationId: encodeURIComponent(organizationId) }),
  };

  const {
    wrapClient: deletePrispevokClient,
    status: deletePrispevokStatus,
  } = useClient(deletePrispevokVestnika);

  const handleDelete = async (): Promise<void> => {
    try {
      await deletePrispevokClient(vestnikDetail.uuid);
      navigate(routes.prispevokVestnika.list.to);
    } catch {
      // Error is handled by deletePrispevokStatus
    }
  };

  return (
    <div className="vestnik-detail">
      <Secured
        allowForRoles={[UserRole.REZORTNE_ZBIERKY_IMPORT, UserRole.REZORTNE_ZBIERKY_SPRAVCA]}
        additionalCheck={isOwnVestnikOrganization(pouzivatel, vestnikDetail)}
      >
        <div className="vestnik-detail__actions">
          <div className="vestnik-detail__action">
            <Button
              buttonStyle="third"
              size="large"
              iconBefore={iconEdit}
              onClick={() => navigate(routes.prispevokVestnika.update.withParams(vestnikDetail.uuid))}
            >
              Upraviť
            </Button>
          </div>

          <div className="vestnik-detail__action">
            <ConfirmModal
              question="Naozaj si prajete odstrániť zvolený príspevok vestníka?"
              cancelButtonText="Nie"
              confirmButton={(
                <Button buttonStyle="negative" size="large">Áno</Button>
              )}
              openButton={(
                <Button
                  buttonStyle="negative"
                  size="large"
                  iconBefore={iconBin}
                  loading={deletePrispevokStatus.loading}
                >
                  Zmazať
                </Button>
              )}
              doAfterConfirm={handleDelete}
            />
          </div>
        </div>
      </Secured>
      <PrispevokVestnikaDetailRow
        header="Autor"
        data={vestnikDetail.vestnik?.skratenyNazov}
      />
      <PrispevokVestnikaDetailRow
        header="Číslo príspevku"
        data={vestnikDetail.cislo}
      />
      <PrispevokVestnikaDetailRow
        header="Dátum zverejnenia"
        data={mapDate(vestnikDetail.datumZverejnenia)}
      />
      <PrispevokVestnikaDetailRow
        header="Dátum účinnosti"
        data={mapDate(vestnikDetail.datumUcinnosti)}
      />
      <PrispevokVestnikaDetailRow
        header="Krátky názov"
        data={vestnikDetail.kratkyNazov}
      />
      <PrispevokVestnikaDetailRow
        header="Vykonáva predpisy ZZ"
        vykonavaciePredpisy={vestnikDetail.vykonavaciePredpisy}
      />
      <PrispevokVestnikaDetailRow
        header="Mení príspevky"
        prispevkyVestnika={vestnikDetail.meniPrispevky}
      />
      <PrispevokVestnikaDetailRow
        header="Ruší príspevky"
        prispevkyVestnika={vestnikDetail.rusiPrispevky}
      />
      <PrispevokVestnikaDetailRow
        header="Popis"
        data={vestnikDetail.popis}
      />
      {hlavnyDokument && (
        <>
          <Heading className="vestnik-detail__priloha-nazov" size="m">Hlavný dokument</Heading>
          <UploadedFile
            file={new File([''], hlavnyDokument.originalFilename)}
            fileSize={hlavnyDokument.fileSize}
            onDownload={() => handleDownloadPriloha(hlavnyDokument.uuid, hlavnyDokument.originalFilename, { headers })}
            inputFilename={hlavnyDokument.nazovSuboru}
          />
        </>

      )}
      {prilohy.length > 0 && (
        <>
          <Heading className="vestnik-detail__priloha-nazov" size="m">Prílohy</Heading>
          {prilohy.map((priloha) => (
            <UploadedFile
              key={priloha.originalFilename}
              file={new File([''], priloha.originalFilename)}
              fileSize={priloha.fileSize}
              onDownload={() => handleDownloadPriloha(priloha.uuid, priloha.originalFilename, { headers })}
              inputFilename={priloha.nazovSuboru}
            />
          ))}
        </>
      )}
    </div>
  );
};

export default PrispevokVestnikaDetail;
